/* Summit navigation: native links and details, with progressive mobile disclosure. */
(() => {
  function setup(scope = document) {
    scope.querySelectorAll('[data-sm-header]').forEach(header => {
      if (header.dataset.smReady) return;
      header.dataset.smReady = 'true';
      const toggle = header.querySelector('[data-sm-toggle]');
      const navigation = header.querySelector('#SummitNavigation');
      const groups = [...header.querySelectorAll('.sm-nav-group')];
      const close = (returnFocus = false) => {
        toggle.setAttribute('aria-expanded', 'false');
        navigation.classList.remove('is-open');
        groups.forEach(group => group.open = false);
        if (returnFocus) toggle.focus();
      };
      toggle.addEventListener('click', () => {
        const open = toggle.getAttribute('aria-expanded') !== 'true';
        toggle.setAttribute('aria-expanded', String(open));
        navigation.classList.toggle('is-open', open);
      });
      groups.forEach(group => group.addEventListener('toggle', () => {
        if (group.open) groups.filter(other => other !== group).forEach(other => other.open = false);
      }));
      header.addEventListener('keydown', event => {
        if (event.key !== 'Escape') return;
        const activeGroup = groups.find(group => group.open && group.contains(document.activeElement));
        if (activeGroup) { activeGroup.open = false; activeGroup.querySelector('summary').focus(); }
        else close(true);
      });
      header.addEventListener('focusout', event => {
        if (event.relatedTarget && !header.contains(event.relatedTarget)) groups.forEach(group => group.open = false);
      });
      document.addEventListener('click', event => { if (!header.contains(event.target)) close(); });
    });
  }
  setup();
  document.addEventListener('shopify:section:load', event => setup(event.target));
})();

/* The same Instagram-sourced agenda powers the homepage and the events page. */
(() => {
  function setupAgendas(scope = document) {
    scope.querySelectorAll('[data-sm-agenda]').forEach(agenda => {
      if (agenda.dataset.agendaReady) return;
      const panels = [...agenda.querySelectorAll('[data-sm-event]')];
      if (!panels.length) return;
      agenda.dataset.agendaReady = 'true';
      const locale = agenda.dataset.locale === 'id' ? 'id-ID' : 'en-GB';
      const now = new Date();
      const today = new Intl.DateTimeFormat('en-CA', {timeZone: 'Asia/Jakarta', year:'numeric',month:'2-digit',day:'2-digit'}).format(now);
      const events = panels.map(panel => ({panel, id:panel.dataset.smEvent, title:panel.dataset.title, start:panel.dataset.start.slice(0,10), last:panel.dataset.lastDate, end:new Date(panel.dataset.end)})).sort((a,b) => a.start.localeCompare(b.start));
      const next = events.find(event => event.end > now);
      let active = next || null;
      let month = (next ? next.start : today).slice(0,7);
      const minimum = events[0].start.slice(0,7);
      const maximum = [today.slice(0,7), ...events.map(event => event.last.slice(0,7))].sort().at(-1);
      const calendar = agenda.querySelector('[data-sm-calendar]');
      const days = agenda.querySelector('[data-sm-days]');
      const list = agenda.querySelector('[data-sm-date-list]');
      const title = agenda.querySelector('[data-sm-month-title]');
      const empty = agenda.querySelector('[data-sm-event-empty]');
      const dateAt = value => new Date(value+'T12:00:00Z');
      const monthDate = () => dateAt(month+'-01');
      const shortDate = value => new Intl.DateTimeFormat(locale,{day:'numeric',month:'short',timeZone:'UTC'}).format(dateAt(value));
      const labelDate = value => new Intl.DateTimeFormat(locale,{weekday:'long',day:'numeric',month:'long',year:'numeric',timeZone:'UTC'}).format(dateAt(value));
      const inMonth = event => event.start.slice(0,7) <= month && event.last.slice(0,7) >= month;
      const select = (event, focusKind) => {
        active=event;month=event.start.slice(0,7);render();
        if (focusKind === 'date') days.querySelector('[aria-pressed="true"]')?.focus();
        if (focusKind === 'list') list.querySelector('[aria-pressed="true"]')?.focus();
      };
      const weekdays = agenda.querySelector('[data-sm-weekdays]');
      for (let i=0;i<7;i++) {
        const label=document.createElement('span');
        label.textContent=new Intl.DateTimeFormat(locale,{weekday:'short',timeZone:'UTC'}).format(new Date(Date.UTC(2026,5,1+i)));
        weekdays.append(label);
      }
      events.forEach(event => {
        const past = event.end <= now;
        event.panel.dataset.state = past ? 'past' : 'upcoming';
        const status=event.panel.querySelector('[data-sm-status]');
        status.textContent=past ? status.dataset.past : status.dataset.upcoming;
        const booking=event.panel.querySelector('[data-sm-booking]');
        if (booking) booking.hidden=past;
      });
      function render() {
        panels.forEach(panel => panel.hidden = !active || panel !== active.panel);
        empty.hidden=Boolean(active);
        title.textContent=new Intl.DateTimeFormat(locale,{month:'long',year:'numeric',timeZone:'UTC'}).format(monthDate());
        agenda.querySelector('[data-sm-month="-1"]').disabled=month<=minimum;
        agenda.querySelector('[data-sm-month="1"]').disabled=month>=maximum;
        days.replaceChildren();list.replaceChildren();
        const first=monthDate();
        const offset=(first.getUTCDay()+6)%7;
        for (let i=0;i<offset;i++) days.append(document.createElement('span'));
        const count=new Date(Date.UTC(first.getUTCFullYear(),first.getUTCMonth()+1,0)).getUTCDate();
        for (let day=1;day<=count;day++) {
          const date=month+'-'+String(day).padStart(2,'0');
          const matches=events.filter(event => date>=event.start && date<=event.last);
          const cell=document.createElement(matches.length ? 'button' : 'span');
          cell.textContent=String(day);
          if(date===today) cell.setAttribute('aria-current','date');
          if(matches.length) {
            cell.type='button';
            cell.setAttribute('aria-label',labelDate(date)+': '+matches.map(event=>event.title).join(', '));
            cell.setAttribute('aria-pressed',String(matches.includes(active)));
            cell.addEventListener('click',()=>select(matches[0], 'date'));
          }
          days.append(cell);
        }
        events.slice().reverse().forEach(event => {
          const button=document.createElement('button');button.type='button';
          button.setAttribute('aria-pressed',String(event===active));
          const date=document.createElement('span');date.textContent=shortDate(event.start);
          const name=document.createElement('span');name.textContent=event.title;
          const state=document.createElement('small');
          const status=event.panel.querySelector('[data-sm-status]');state.textContent=status.textContent;
          name.append(state);button.append(date,name);button.addEventListener('click',()=>select(event, 'list'));list.append(button);
        });
      }
      agenda.querySelectorAll('[data-sm-month]').forEach(button => button.addEventListener('click',()=>{
        const date=monthDate();date.setUTCMonth(date.getUTCMonth()+Number(button.dataset.smMonth));
        month=date.toISOString().slice(0,7);active=events.find(inMonth)||null;render();
      }));
      const jump=agenda.querySelector('[data-sm-next-event]');
      jump.hidden=!next;jump.addEventListener('click',()=>{if(next)select(next);});
      const requested=events.find(event=>'#'+event.id===window.location.hash);
      if(requested) {active=requested;month=requested.start.slice(0,7);}
      render();calendar.hidden=false;
    });
  }
  setupAgendas();
  document.addEventListener('shopify:section:load',event=>setupAgendas(event.target));
})();

/* Bring the selected event into view when the calendar stacks below it. */
document.addEventListener('click', event => {
  const choice = event.target.closest('[data-sm-date-list] button, [data-sm-days] button, [data-sm-next-event]');
  if (!choice || !window.matchMedia('(max-width: 900px)').matches) return;
  const agenda = choice.closest('[data-sm-agenda]');
  requestAnimationFrame(() => {
    const panel = agenda.querySelector('[data-sm-event]:not([hidden])');
    if (panel) {
      const heading = panel.querySelector('h3');
      heading.setAttribute('tabindex', '-1');
      heading.focus({preventScroll:true});
      panel.scrollIntoView({block:'start',behavior:window.matchMedia('(prefers-reduced-motion: reduce)').matches?'instant':'smooth'});
    }
  });
}, true);
